import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';

// Create a simple toast system
export const toastEvents = {
  listeners: [],
  emit: (toast) => {
    toastEvents.listeners.forEach(listener => listener(toast));
  },
  subscribe: (listener) => {
    toastEvents.listeners.push(listener);
    return () => {
      toastEvents.listeners = toastEvents.listeners.filter(l => l !== listener);
    };
  }
};

// Helper function to show toasts from anywhere
export const showToast = (message, type = 'info', duration = 5000) => {
  toastEvents.emit({ id: Date.now(), message, type, duration });
};

function ToastNotification() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const unsubscribe = toastEvents.subscribe((toast) => {
      setToasts(prev => [...prev, toast]);
      
      // Auto-remove after duration
      setTimeout(() => {
        setToasts(prev => prev.filter(t => t.id !== toast.id));
      }, toast.duration || 5000);
    });

    return unsubscribe;
  }, []);

  const removeToast = (id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const getIcon = (type) => {
    switch (type) {
      case 'success': return '✅';
      case 'error': return '❌';
      case 'warning': return '⚠️';
      case 'agent': return '🎯';
      case 'task': return '📋';
      default: return 'ℹ️';
    }
  };

  return (
    <div className="toast-container">
      {toasts.map((toast) => (
        <div 
          key={toast.id} 
          className={`toast toast-${toast.type}`}
          onClick={() => removeToast(toast.id)}
        >
          <span className="toast-icon">{getIcon(toast.type)}</span>
          <span className="toast-message">{toast.message}</span>
          <button className="toast-close" onClick={() => removeToast(toast.id)}>×</button>
        </div>
      ))}

      <style>{`
        .toast-container {
          position: fixed;
          top: 20px;
          right: 20px;
          z-index: 10000;
          display: flex;
          flex-direction: column;
          gap: 10px;
          max-width: 400px;
        }

        .toast {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 14px 18px;
          background: var(--bg-secondary);
          border: 1px solid var(--border-color);
          border-radius: 8px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
          cursor: pointer;
          animation: slideIn 0.3s ease-out;
          min-width: 280px;
        }

        .toast:hover {
          transform: translateX(-5px);
        }

        .toast-success {
          border-left: 4px solid #00ff88;
        }

        .toast-error {
          border-left: 4px solid #ff4444;
        }

        .toast-warning {
          border-left: 4px solid #ffaa00;
        }

        .toast-agent {
          border-left: 4px solid var(--neon-green);
          background: rgba(255, 34, 68, 0.1);
        }

        .toast-task {
          border-left: 4px solid var(--neon-cyan);
        }

        .toast-icon {
          font-size: 18px;
        }

        .toast-message {
          flex: 1;
          color: var(--text-primary);
          font-size: 14px;
        }

        .toast-close {
          background: none;
          border: none;
          color: var(--text-muted);
          font-size: 20px;
          cursor: pointer;
          padding: 0;
          line-height: 1;
        }

        .toast-close:hover {
          color: var(--text-primary);
        }

        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default ToastNotification;
